// react and js
import React, { forwardRef, useImperativeHandle, useState, useRef } from "react";
import { useFormik, FormikProvider } from "formik";
import PropTypes from "prop-types";

// mui
import { Stack, Box, IconButton, DialogTitle, DialogContent, DialogActions, Typography, FormControl, Select } from "@mui/material";

// components
import ModalDialog from "../../../components/ModalDialog";
import RequestMessage from "../../../components/RequestMessage";
import Iconify from "../../../components/Iconify";
import SmartParameter from "../../../components/SmartParameter";

// theme
import { B1Tiny, ModalActionsLoadingButton, ModalContentOutlinedInput, ModalContentMenuItem, ModalContentSwitch } from "../../../theme/styled";

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export const MiscellaneousDefaults = {
  nDrawUnequalSymbols: false,
  nDrawSecondTimeframe: "-2",
  nDrawChartAnalyzerPanel: false,
};

// ----------------------------------------------------------------------
// Section
// ----------------------------------------------------------------------
export function MiscellaneousRenderParameters(professionalMode, config) {
  const renderParam = (label, value, leftWidth = "auto", rightWidth = "full") => (
    <Box sx={{ display: "flex", width: "100%", gap: 1 }}>
      <Typography
        variant="contentTiny"
        sx={{
          fontWeight: 600,
          flex: `1 1 ${leftWidth}`,
          maxWidth: leftWidth,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="contentTiny"
        sx={{
          flex: `1 1 ${rightWidth}`,
          maxWidth: rightWidth,
          wordBreak: "break-word",
          textAlign: "right",
        }}
      >
        {value}
      </Typography>
    </Box>
  );

  // Helper function to get switch display values (On/Off)
  const getSwitchDisplay = (value) => (value === true ? "On" : "Off");

  // Helper function to get timeframe display
  const getTimeframeDisplay = (value) => {
    const timeframes = {
      "-2": "Off",
      "0": "M1",
      "1": "M5",
      "2": "M15",
      "3": "M30",
      "4": "H1",
      "5": "H4",
      "6": "D1",
      "7": "W1",
      "8": "MN1",
    };
    return timeframes[value] || "Off";
  };

  return (
    <Stack spacing={0.5}>
      {/* Draw second timeframe is always visible */}
      {renderParam("Draw second timeframe", getTimeframeDisplay(config.nDrawSecondTimeframe))}
      {professionalMode === true ? (
        <>
          {renderParam("Draw unequal symbols", getSwitchDisplay(config.nDrawUnequalSymbols))}
          {renderParam("Draw chart analyzer panel", getSwitchDisplay(config.nDrawChartAnalyzerPanel))}
        </>
      ) : null}
    </Stack>
  );
}

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
export const MiscellaneousConfigure = forwardRef(({ onSave, initialData = null, professionalMode = null }, ref) => {
  // ----------------------------------------------------------------------
  // Properties
  // ----------------------------------------------------------------------
  MiscellaneousConfigure.propTypes = {
    onSave: PropTypes.func.isRequired,
    initialData: PropTypes.object,
    professionalMode: PropTypes.any,
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const [scroll, setScroll] = useState("paper");
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const autoCloseTimeoutRef = useRef(null);

  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [warning, setWarning] = useState(false);
  const [returnMessage, setReturnMessage] = useState("");
  
  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  useImperativeHandle(ref, () => ({
    openDialog: () => {
      setOpen(true);
    },
    closeDialog: () => {
      setOpen(false);
    },
  }));

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleReset = () => {
    // Clear all errors and touched state
    setErrors({});
    setTouched({});
    // Apply all the default values to the form
    Object.keys(MiscellaneousDefaults).forEach((key) => {
      setFieldValue(key, MiscellaneousDefaults[key]);
    });
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleClose = () => {
    // Clear any existing auto-close timeout
    if (autoCloseTimeoutRef.current) {
      clearTimeout(autoCloseTimeoutRef.current);
      autoCloseTimeoutRef.current = null;
    }

    setOpen(false);
    setError(false);
    setSuccess(false);
    setWarning(false);
    setReturnMessage("");
    resetForm();
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const formik = useFormik({
    initialValues: {
      nDrawUnequalSymbols: initialData?.nDrawUnequalSymbols ?? MiscellaneousDefaults.nDrawUnequalSymbols,
      nDrawSecondTimeframe: initialData?.nDrawSecondTimeframe ?? MiscellaneousDefaults.nDrawSecondTimeframe,
      nDrawChartAnalyzerPanel: initialData?.nDrawChartAnalyzerPanel ?? MiscellaneousDefaults.nDrawChartAnalyzerPanel,
      },
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values, { setSubmitting }) => {
      setTimeout(() => {
        try {
          onSave(values);
          setSuccess(true);
          setReturnMessage("Configuration saved successfully!");
          // autoCloseTimeoutRef.current = setTimeout(() => {
          //  handleClose();
          // }, 1500);
        } catch (err) {
          setError(true);
          setReturnMessage("Failed to save configuration.");
        }
        setSubmitting(false);
      }, 100);
    },
  });

  const { values, errors, touched, isSubmitting, handleSubmit, getFieldProps, setFieldValue, setFieldTouched, setErrors, setTouched, resetForm } = formik;

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleClickOpen = (scrollType) => () => {
    setOpen(true);
    setScroll(scrollType);
  };

    // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  let saveAndCloseDisabled = false;

  const hasValidationErrors = () => Object.keys(formik.errors).length > 0;
  if (hasValidationErrors()) {
    saveAndCloseDisabled = true;
  }
  
  // ----------------------------------------------------------------------
  // Main component
  // ----------------------------------------------------------------------
  return (
    <>
      <B1Tiny fullWidth disableRipple={Boolean(true)} onClick={handleClickOpen("paper")}>
        Configure miscellaneous
      </B1Tiny>

      <ModalDialog
        open={open}
        handleClose={handleClose}
        scroll={scroll}
        outlet={
          <>
            <DialogTitle
              id="dialog-title"
              sx={{
                background: "#F2F2F2",
                textTransform: "none",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                alignContent: "center",
                py: 1,
                pl: 2,
                pr: 1,
              }}
            >
              <Typography variant="contentBig">Configure miscellaneous</Typography>

              <IconButton onClick={handleClose} sx={{ borderRadius: "10px" }}>
                <Iconify icon="eva:close-outline" minWidth={25} minHeight={25} color="#2E2E2E" />
              </IconButton>
            </DialogTitle>

            <FormikProvider value={formik}>
              <DialogContent
                dividers
                sx={{
                  background: "#F2F2F2",
                  borderTop: "1px solid #C8C8C8",
                  borderBottom: "0px solid #C8C8C8",
                  px: 2,
                  pt: 2,
                  pb: 1,
                  width: "100%",
                  boxSizing: "border-box",
                  overflowY: "scroll",
                  overflowX: "hidden",
                  // Custom scrollbar styling
                  "&::-webkit-scrollbar": {
                    width: "16px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#e0e0e0",
                    borderRadius: "6px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#888888",
                    borderRadius: "6px",
                    border: "2px solid #e0e0e0",
                  },
                  "&::-webkit-scrollbar-thumb:hover": {
                    backgroundColor: "#666666",
                  },
                  // Firefox scrollbar styling
                  scrollbarWidth: "thin",
                  scrollbarColor: "#888888 #e0e0e0",
                }}
              >
                <Stack spacing={2}>
                  {/* ---------------------------------------------------------------------- */}
                  {/* Grid */}
                  {/* ---------------------------------------------------------------------- */}

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 2,
                    }}
                  >
                    {/* ---------------------------------------------------------------------- */}
                    {/* Parameter configuration */}
                    {/* nDrawSecondTimeframe */}
                    {/* ---------------------------------------------------------------------- */}
                    <Box>
                      <SmartParameter
                        value="Draw second timeframe"
                        helpReference="HandleMiscellaneous"
                        helpText=""
                        helpLink=""
                        fieldName="nDrawSecondTimeframe"
                        touched={touched}
                        errors={errors}
                      />
                      <FormControl fullWidth>
                        <Select
                          {...getFieldProps("nDrawSecondTimeframe")}
                          input={<ModalContentOutlinedInput />}
                          error={Boolean(touched.nDrawSecondTimeframe && errors.nDrawSecondTimeframe)}
                        >
                          <ModalContentMenuItem value="-2">Off</ModalContentMenuItem>
                          <ModalContentMenuItem value="0">M1</ModalContentMenuItem>
                          <ModalContentMenuItem value="1">M5</ModalContentMenuItem>
                          <ModalContentMenuItem value="2">M15</ModalContentMenuItem>
                          <ModalContentMenuItem value="3">M30</ModalContentMenuItem>
                          <ModalContentMenuItem value="4">H1</ModalContentMenuItem>
                          <ModalContentMenuItem value="5">H4</ModalContentMenuItem>
                          <ModalContentMenuItem value="6">D1</ModalContentMenuItem>
                          <ModalContentMenuItem value="7">W1</ModalContentMenuItem>
                          <ModalContentMenuItem value="8">MN1</ModalContentMenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    {professionalMode === true ? (
                      <>
                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* nDrawUnequalSymbols */}
                        {/* ---------------------------------------------------------------------- */}
                        <Box>
                          <SmartParameter value="Draw unequal symbols" helpReference="HandleMiscellaneous" helpText="" helpLink="" fieldName="nDrawUnequalSymbols" touched={touched} errors={errors} />
                          <ModalContentSwitch
                            sx={{ mt: "12px" }}
                            checked={values.nDrawUnequalSymbols}
                            onChange={(event) => {
                              setFieldValue("nDrawUnequalSymbols", event.target.checked);
                            }}
                          />
                        </Box>

                        {/* ---------------------------------------------------------------------- */}
                        {/* Parameter configuration */}
                        {/* nDrawChartAnalyzerPanel */}
                        {/* ---------------------------------------------------------------------- */}
                        <Box>
                          <SmartParameter value="Draw chart analyzer panel" helpReference="HandleMiscellaneous" helpText="" helpLink="" fieldName="nDrawChartAnalyzerPanel" touched={touched} errors={errors} />
                          <ModalContentSwitch
                            sx={{ mt: "12px" }}
                            checked={values.nDrawChartAnalyzerPanel}
                            onChange={(event) => {
                              setFieldValue("nDrawChartAnalyzerPanel", event.target.checked);
                            }}
                          />
                        </Box>
                      </>
                    ) : null}
                  </Box>
                </Stack>
              </DialogContent>

              <Box sx={{ px: 1, pt: 1, mb: -1, background: "#F2F2F2" }}>
                <RequestMessage wrapperRef={wrapperRef} error={error} warning={warning} success={success} returnMessage={returnMessage} />
              </Box>

              <DialogActions
                sx={{
                  background: "#F2F2F2",
                  p: 2,
                  pt: 0,
                  display: "flex",
                  alignItems: "flex-start",
                  flexDirection: "column",
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <Box sx={{ pt: 1, width: "100%", display: "flex", gap: 1 }}>
                  <ModalActionsLoadingButton
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleReset}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Reset
                  </ModalActionsLoadingButton>

                  <ModalActionsLoadingButton
                    fullWidth
                    disabled={saveAndCloseDisabled}
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleSubmit}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Save
                  </ModalActionsLoadingButton>

                  <ModalActionsLoadingButton
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    onClick={handleClose}
                    loading={isSubmitting}
                    sx={{ mt: 1 }}
                  >
                    Close
                  </ModalActionsLoadingButton>
                </Box>
              </DialogActions>
            </FormikProvider>
          </>
        }
      />
    </>
  );
});
